import { Injectable } from "@angular/core";
import { formatDate } from "@angular/common";
import { convertEpochToReadableISTDateTime } from "./date-time-utils";
import { DateUtils } from "./date-utils";
import { TableDataType, TableMeta } from "../models/table-meta.interface";
import { AdvancedFilterFieldInfo } from "../models/advanced-filter-config.interface";
import { TableListRequest } from "../models/table-list-request.interface";
import { IdName } from "../models/id-name.interface";
import { CURRENCY_CODE, LOCALE, LOCALES } from "../constants/app-constants";
import { parseExpression } from "cron-parser";

@Injectable({
  providedIn: "root"
})
export class CommonUtils {

  static formatData(value: any, dataType: string): string {
    if (value === undefined || value === null) {
      return "";
    }
    const type = dataType?.toUpperCase();

    if (type === "DATETIME_EPOCH") {
      return convertEpochToReadableISTDateTime(value);
    } else if (type === "DATE") {
      return DateUtils.formatDate(value, "dd/MM/yyyy");
    } else if (type === "DATETIME") {
      return DateUtils.formatDate(value, "dd/MM/yy hh:mm a");
    } else if (type === "BOOLEAN") {
      return value ? "Yes" : "No";
    } else if (type === "ARRAY" && Array.isArray(value)) {
      return value.join(", ");
    }
    return String(value);
  }

  static getNestedValue(obj: any, path: string): any {
    if (!obj || !path) return "";

    // Remove trailing dot(s)
    const cleanPath = path.replace(/\.+$/, "");

    if (!cleanPath.includes(".")) {
      return obj[cleanPath];
    }

    return cleanPath.split(".").reduce(
      (acc, part) => (acc != null ? acc[part] : undefined),
      obj
    );
  }

  static setNestedValue(obj: any, path: string, value: any): void {
    if (!obj || !path) return;

    if (!path.includes(".")) {
      obj[path] = value;
      return;
    }

    const parts = path.split(".");
    const lastPart = parts.pop()!;
    const target = parts.reduce((acc, part) => {
      if (!acc[part]) {
        acc[part] = {};
      }
      return acc[part];
    }, obj);

    target[lastPart] = value;
  }

  static getFilterFieldList(fieldInfo: any[]): AdvancedFilterFieldInfo[] {
    return fieldInfo.map((fieldInfo: any) => {
      // Check if operatorToFilterMap exists and is not a Map instance
      const operatorToFilterMap = fieldInfo.operatorToFilterMap;
      const processedMap = operatorToFilterMap && !(operatorToFilterMap instanceof Map)
        ? new Map(Object.entries(operatorToFilterMap))
        : operatorToFilterMap;

      return {
        ...fieldInfo,
        operatorToFilterMap: processedMap
      };
    });
  }

  static deepCopyJson(json: any) {
    return JSON.parse(JSON.stringify(json));
  }

  static isNotNil(val: any) {
    return val !== undefined && val !== null;
  }

  static buildIdFilterRequest(ids: string[], filterName?: string): TableListRequest {
    return {
      where: {
        filterConditions: [
          {
            name: filterName ?? "ID_CONTAINS",
            alias: filterName ?? "ID_CONTAINS",
            value: ids
          }
        ],
        expression: filterName ?? "ID_CONTAINS"
      },
      limit: ids.length,
      page: 0
    };
  }

  static toIDName(formValue: any): IdName | undefined {
    if (formValue?.key && formValue?.value) {
      return { id: formValue.key, name: formValue.value };
    } else if (formValue?.id && formValue?.name) {
      return { id: formValue.id, name: formValue.name };
    }
    return undefined;
  }

  static toKeyValue(input: IdName): { key: string, value: string } | null {
    if (!input || !input.id || !input.name) return null;
    return { key: input.id, value: input.name };
  }

  /**
   * Downloads a blob as a file in the browser.
   *
   * @param blob - The blob data (e.g., from API response)
   * @param filename - Desired name for the downloaded file
   */
  static downloadBlobAsFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  static savePdf(base64Data: string, fileName: string): void {
    console.log("base64Data", base64Data);
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();

    // Cleanup
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  }

  static saveImageBase64(base64Data: string, fileName = "image.jpg"): void {
    if (!base64Data) return;

    const link = document.createElement("a");
    link.href = `data:image/jpeg;base64,${base64Data}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  static addDecimals(a: number, b: number, decimals: number = 2): number {
    const factor = Math.pow(10, decimals);
    return (Math.round(a * factor) + Math.round(b * factor)) / factor;
  }

  static roundToDecimals(value: number, decimals: number = 2): number {
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  static formatCurrency(value: any, locale: string = LOCALE, currency: string = CURRENCY_CODE): string {
    if (value == null || isNaN(value)) {
      return "";
    }

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value));
  }

  /**
   * Returns the next run Date for a cron expression in a given timezone.
   * @param cronExpression cron string (supports seconds)
   * @param timeZone IANA timezone (e.g., "Asia/Kolkata")
   * @param from optional start time; defaults to now
   */
  static getNextCronRunDate(cronExpression: string, timeZone: string, from: Date = new Date()): Date | null {
    try {
      const interval = parseExpression(cronExpression, { currentDate: from, tz: timeZone });
      return interval.next().toDate();
    } catch (err) {
      console.error("Failed to parse cron expression", err);
      return null;
    }
  }

  /**
   * Downloads an XLSX file from a blob response.
   * Extracts filename from Content-Disposition header if available, otherwise uses provided filename.
   *
   * @param blob - The blob data (e.g., from API response)
   * @param filename - Default filename if not found in headers
   */
  static downloadXlsxFile(blob: Blob, filename: string = "report.xlsx"): void {
    CommonUtils.downloadBlobAsFile(blob, filename);
  }

  /**
   * Extracts filename from Content-Disposition header.
   *
   * @param contentDisposition - Content-Disposition header value
   * @returns Extracted filename or null if not found
   */
  static extractFilenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      let filename = filenameMatch[1].replace(/['"]/g, "");
      // Decode URI if needed
      try {
        filename = decodeURIComponent(filename);
      } catch (e) {
        // If decoding fails, use the original filename
      }
      return filename;
    }

    return null;
  }
}
